import { modal } from 'components/Modals/ModalManager';
import { ShareableRoutineFolder } from 'hevy-shared';
import { makeAutoObservable } from 'mobx';
import toast from 'react-hot-toast';
import { stores } from 'state/stores';
import API from 'utils/API';
import { fireAndForget } from 'utils/async';
import { sendEvent } from 'utils/events';
import { log } from 'utils/log';
import { copyToClipboard } from 'utils/clipboard';

interface Props {
  folder?: ShareableRoutineFolder;
  isAuthenticated: boolean;
}

export class FolderDetailViewModel {
  folder?: ShareableRoutineFolder;
  isAuthenticated: boolean;
  isError: boolean = false;
  isFolderSaved: boolean = false;
  isSavingFolder: boolean = false;

  constructor({ folder, isAuthenticated }: Props) {
    log('FolderDetailViewModel constructor');

    makeAutoObservable(this);

    this.isAuthenticated = isAuthenticated;
    if (folder) {
      this.folder = folder;
    } else {
      this.isError = true;
    }
  }

  get isFolderEmpty() {
    return this.folder?.routines.length === 0;
  }

  get isUsersFolder() {
    if (stores.auth.isAuthenticated && this.folder) {
      const folder = stores.routineFolders.getFolderById(this.folder.id);
      return !!folder;
    }

    return false;
  }

  onSaveFolder = async () => {
    if (!this.folder) return;

    const routineLength = stores.routines.routines.length + this.folder.routines.length;

    if (!stores.subscription.isPro && routineLength >= 4) {
      modal.paywall({
        type: 'routine-limit',
        onCancel: () => null,
      });
      return;
    }

    const save = async (folder: ShareableRoutineFolder) => {
      try {
        this.isSavingFolder = true;
        const response = await API.postRoutineFolder({ title: folder.title });
        const routines = folder.routines.map(r =>
          API.postRoutineCopy({
            routineId: r.id,
            ignoreValues: true,
            index: r.index,
            folderId: response.data.id,
          }),
        );
        await Promise.all(routines);
        await fireAndForget([stores.routines.fetch()]);
        this.isSavingFolder = false;
        this.isFolderSaved = true;
        toast.success('Folder saved!');
      } catch (e) {
        toast.error('There was an error saving the folder');
      }
    };

    if (this.isFolderSaved || this.isUsersFolder) {
      modal.confirmation({
        title: `Save Folder Again?`,
        subtitle: `You've already saved this folder. Would you like to make another copy?`,
        submitButtonTitle: 'Yes!',
        onConfirm: () => save(this.folder!),
      });
    } else {
      save(this.folder);
    }
  };

  onCopyUrl = () => {
    if (!this.folder) return;
    sendEvent('folderDetail_copyLink', { folderId: this.folder.id });
    copyToClipboard(window.location.href);
    toast.success('Copied to clipboard');
  };

  get title(): string {
    return this.folder?.title || '';
  }

  get username(): string {
    return this.folder?.username || '';
  }

  get profilePic(): string {
    return this.folder?.profile_pic || '';
  }

  get existingUserSaveButtonTitle(): string {
    return this.isFolderSaved ? 'Saved' : 'Save Folder';
  }
}
